import React, { FC, Fragment, useState } from 'react';
import PlanCardStyled from './PlanCardStyled';
import Button from '~/components/Button';
import { Product, SelectedProductsState, Feature, Modals, PlanCardsMetadata } from '~/types/products';
import { Buttons } from '../../../types/preamp';
import { ChevronDown, Check, Wifi, InfoCircle } from '~/components/Icons/fill/';
import PlanCardBanner from './components/PlanCardBanner';
import { Option } from '~/types/products';
import { useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import FeatureItem from './components/FeatureItem';
import { trackEvent } from '~/helpers/tracking';
import useWindowSize from '~/hooks/useWindowSize';
import { DEFAULT_FUSE_NUMBER } from '~/constants/config/common';
import addMoney from '~/helpers/addMoney';
import slugify from '~/helpers/slugify';
import Tooltip from '~/components/Tooltip';
import Cookies from 'js-cookie';
import { getTooltip } from '~/helpers/legal';
import getShortLegal from '~/helpers/getShortLegal';

interface Props {
  metadata?: PlanCardsMetadata;
  addPhoneState: boolean;
  bundleCost: number;
  bundleFeatures: Feature[];
  product: Product;
  singleProduct: boolean;
  twoProducts?: boolean;
  sweetener: Option;
  isCopper: boolean;
  is500: boolean;
  is2Gig: boolean;
  is5Gig: boolean;
  is7Gig: boolean;
  is1Gig: boolean;
  is200: boolean;
  isCopper2Fiber: boolean;
  selectedState?: SelectedProductsState;
  handleSelectPlan?: (product: Product) => void;
  setActiveModal: (key: Modals) => void;
  setEeroModal: (product: Product) => void;
  setBroadbandConsumerLabelModal: (product: Product) => void;
  buttons: Buttons;
  showPlanCardBanner: boolean;
}

const PlanCard: FC<Props> = ({
  metadata,
  bundleCost: bundCost,
  bundleFeatures,
  product,
  singleProduct,
  twoProducts,
  sweetener,
  selectedState,
  handleSelectPlan,
  setEeroModal,
  setBroadbandConsumerLabelModal,
  isCopper,
  buttons,
  showPlanCardBanner,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { address, products, legal, callInNumber, preamp, monarch } = useSelector((store: ReduxState) => store);
  const state = address?.install?.state || '';
  const { legalMap = {} } = legal || {};

  const {
    dataspeed,
    features: productFeatures,
    is500,
    is1Gig,
    is2Gig,
    is5Gig,
    is7Gig,
    is200,
    name,
    price,
    productId,
    shortLegal,
    sweeteners,
  } = product;

  const features = (() => {
    if (bundleFeatures && productFeatures) return [...productFeatures, ...bundleFeatures];
    if (productFeatures && !bundleFeatures) return productFeatures;
    return [];
  })();

  const selectedPlan = (selectedState?.internet?.productId ?? products?.selected?.internet?.productId) === productId;
  const bundleCost = is2Gig || preamp?.VASVersion ? 0 : bundCost;
  const { mobile } = useWindowSize();
  const isAllconnect = process.env.allconnect == 'true';
  const addressOfferType = Cookies.get('addressOfferType') ?? 'default';

  const handleCallToOrder = () => {
    trackEvent({ action: 'callToOrder' });
    window.location.href = `tel:${DEFAULT_FUSE_NUMBER}`;
  };

  const agAddressType = (() => {
    if (['OH', 'PA', 'NV'].includes(state)) return 'ag-all';
    if (['MN'].includes(state)) return 'ag-mn';
    return 'ag-none';
  })();

  const togglePlanFeatures = () => {
    setExpanded(!expanded);
    trackEvent({
      action: 'elementClicked',
      data: {
        text: `${name} Internet`,
        elementType: 'BUTTON',
        location: 'product_selection_page',
        position: `product_cards_plan_details_${expanded ? 'less' : 'more'}`,
      },
    });
  };

  const { bannerEnabled: enabled } = metadata?.[dataspeed?.downloadInMbps] || {};
  const bannerEnabled = enabled !== 'false' && showPlanCardBanner;
  const hideBannerDisplay = sweetener?.value === 'skip' && is2Gig;

  const indianaOffer = address?.install?.dma === '509' && monarch?.indianaOffer;

  return (
    <PlanCardStyled
      expanded={expanded}
      singleProduct={singleProduct}
      twoProducts={twoProducts}
      agAddressType={agAddressType}
      bannerEnabled={bannerEnabled}
      hasHover={Boolean(sweeteners.eero) && sweetener?.value === 'visa'}
      hideBannerDisplay={hideBannerDisplay}
      isCopper={isCopper}
      data-testid={slugify(name)}
    >
      {Object.keys(sweeteners).length > 0 && (
        <div className="plan-card__banner-wrapper" data-testid="banner-hover">
          {bannerEnabled && (
            <PlanCardBanner
              legalMap={legalMap}
              sweeteners={sweeteners}
              sweetener={sweetener}
              is500={is500}
              is1Gig={is1Gig}
              is2Gig={is2Gig}
              is5Gig={is5Gig}
              is7Gig={is7Gig}
              is200={is200}
            />
          )}
        </div>
      )}

      <div className={`plan-card__header ${dataspeed?.downloadInMbps === 2000 && 'plan-card__2gig'}`}>
        <div className="plan-card__speed">
          {!isCopper && dataspeed ? (
            <Fragment>
              {name.replace('Internet', '').trim()}
              <small>
                {dataspeed?.downloadInMbps}/{dataspeed?.uploadInMbps} Mbps
              </small>
            </Fragment>
          ) : (
            <Fragment>
              Frontier
              <br />
              Internet
            </Fragment>
          )}
        </div>
        {/* TODO: clean up static logic */}
        {dataspeed?.downloadInMbps >= 500 && (
          <div className="plan-card__product-type">
            <span className="plan-card__wifi">
              <Wifi className="icon--fill-wifi" /> Internet
            </span>
          </div>
        )}
        {is2Gig && (
          <div className="plan-card__best-value" data-testid="plan-card-best-value">
            Best value
          </div>
        )}
        {is7Gig && (
          <div className="plan-card__best-value" data-testid="plan-card-best-value">
            New
          </div>
        )}
      </div>
      <div className="plan-card__body">
        <div className={`plan-card__price-legal-container ${isCopper ? 'is-copper' : 'is-fiber'} ${agAddressType}`}>
          <div className="plan-card__price-container">
            <sup className="pre">$</sup>
            <div className="plan-card__dollars">
              <span data-testid="plan-card-amount-major">{`${addMoney(price.amountMajor, bundleCost)}`}</span>
              <span className="plan-card__full-price">${addMoney(price.originalAmount, bundleCost)}</span>
            </div>
            <sup className="post" data-testid="plan-card-amount-minor">{`.${price.amountMinor}`}</sup>
            <Tooltip
              position="top"
              theme="light"
              html={
                <div>
                  <div className="tooltip-info">
                    {getTooltip(product, legalMap, Boolean(indianaOffer), addressOfferType)}
                  </div>
                </div>
              }
            >
              <InfoCircle />
            </Tooltip>
          </div>
          <div className="plan-card__legal">
            {shortLegal && (
              <div className="plan-card__legal-text">
                {getShortLegal(product, Boolean(indianaOffer))?.replace('. ETF applies', '')}
              </div>
            )}
          </div>
          {isCopper && (
            <button className="consumer-label" onClick={() => setBroadbandConsumerLabelModal(product)}>
              Broadband Consumer Label
            </button>
          )}
        </div>
        <div className={`plan-card__ctas ${buttons?.call?.enabled === 'false' ? 'is-single' : ''}`}>
          <Button
            className={`plan-card__button${selectedPlan ? ' active icon' : ''}`}
            onClick={() => handleSelectPlan(product)}
            data-testid="plan-card-btn-select"
            buttonConfig={buttons?.select}
            data-chsn-reward-click={!isCopper && `cart ${name?.toLocaleLowerCase()} plan card`}
          >
            {selectedPlan ? (
              <span>
                <Check /> {buttons?.select?.activeText || 'Added to Cart'}
              </span>
            ) : (
              buttons?.select?.text || 'Select Plan'
            )}
          </Button>
          {isAllconnect && mobile && buttons?.call?.enabled !== 'false' && (
            <Button
              as="a"
              href={`tel:${callInNumber}`}
              className="plan-card__button"
              secondary
              onClick={() => handleCallToOrder()}
              buttonConfig={buttons?.call}
            >
              {buttons?.call?.fuseNumber === 'true'
                ? `${callInNumber.replace(/-/g, '.')}`
                : buttons?.call?.text || 'Call to Order'}
            </Button>
          )}
        </div>
        <div className="plan-card__features-container">
          <ul className="plan-card__features">
            {features?.map((feature, index) => (
              <FeatureItem
                key={index}
                {...feature}
                setEeroModal={() => setEeroModal(product)}
                isCopper={products?.broadbandType?.toLowerCase().includes('copper')}
                product={product}
                is2Gig={dataspeed?.downloadInMbps === 2000}
                is5Gig={product?.dataspeed?.downloadInMbps === 5000}
                is7Gig={product?.dataspeed?.downloadInMbps === 7000}
                is500={dataspeed?.downloadInMbps === 500}
                is1Gig={dataspeed?.downloadInMbps > 500 && dataspeed?.downloadInMbps < 2000}
                is200={dataspeed?.downloadInMbps === 200}
              />
            ))}
            {!isCopper && (
              <li className="plan-card__feature">
                <button onClick={() => setBroadbandConsumerLabelModal(product)}>Broadband Consumer Label</button>
              </li>
            )}
          </ul>
        </div>
      </div>
      <button onClick={togglePlanFeatures} className="plan-card__details">
        {expanded ? 'Less' : 'More'} plan details <ChevronDown aria-label="expand" />
      </button>
    </PlanCardStyled>
  );
};

PlanCard.displayName = 'PlanCard';

export default PlanCard;
